import React from 'react';
import { createPortal } from 'react-dom';
import { Check, X, Search, SearchX } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { getAvatarGradient } from '../communication/personModel';
import { SidebarPersonDetail } from './PeopleSidebarItem';

interface PeopleSearchFlyoutProps {
  isSearchOpen: boolean;
  searchCoords: { top: number; right: number } | null;
  searchFlyoutRef: React.RefObject<HTMLDivElement | null>;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  searchInput: string;
  setSearchInput: React.Dispatch<React.SetStateAction<string>>;
  setIsSearchOpen: React.Dispatch<React.SetStateAction<boolean>>;
  filteredPeople: string[];
  totalPeopleCount: number;
  peopleData?: Map<string, SidebarPersonDetail>;
  filterAssignee: string;
  activeVorgangNames?: Set<string>;
  stopWords: Set<string>;
  isFilterActive: boolean;
  onToggleFilter: (name: string) => void;
  onClear: () => void;
}

export const PeopleSearchFlyout: React.FC<PeopleSearchFlyoutProps> = ({
  isSearchOpen,
  searchCoords,
  searchFlyoutRef,
  searchInputRef,
  searchInput,
  setSearchInput,
  setIsSearchOpen,
  filteredPeople,
  totalPeopleCount,
  peopleData,
  filterAssignee,
  activeVorgangNames,
  stopWords,
  isFilterActive,
  onToggleFilter,
  onClear,
}) => {
  if (!isSearchOpen || !searchCoords || typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        ref={searchFlyoutRef}
        initial={{ opacity: 0, x: 8, scale: 0.98 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        exit={{ opacity: 0, x: 6, scale: 0.98 }}
        transition={{ duration: 0.12, ease: 'easeOut' }}
        style={{
          position: 'fixed',
          top: searchCoords.top,
          right: searchCoords.right,
          zIndex: 9999
        }}
        className="w-80 bg-slate-900 border border-slate-700/80 rounded-2xl p-3 shadow-2xl shadow-black/95 ring-1 ring-white/10 flex flex-col gap-2.5 pointer-events-auto text-left"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between pb-2 border-b border-white/10">
          <div className="flex items-center gap-1.5 text-xs font-bold text-slate-200">
            <Search size={14} className="text-indigo-400" />
            <span>Personen durchsuchen</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10.5px] font-medium text-slate-400">
              {filteredPeople.length} von {totalPeopleCount}
            </span>
            <button
              type="button"
              onClick={() => {
                setSearchInput('');
                setIsSearchOpen(false);
              }}
              className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
              title="Schließen (Esc)"
            >
              <X size={13} />
            </button>
          </div>
        </div>

        {/* Search Input Box */}
        <div className="relative flex items-center">
          <Search size={14} className="absolute left-3 text-slate-400 pointer-events-none" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="Name, Organisation, Rolle..."
            className="w-full bg-slate-800 border border-white/15 rounded-xl pl-9 pr-8 py-2 text-xs text-white placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
            onKeyDown={e => {
              if (e.key === 'Escape') {
                if (searchInput) {
                  setSearchInput('');
                } else {
                  setIsSearchOpen(false);
                }
              } else if (e.key === 'Enter') {
                if (filteredPeople.length > 0) {
                  onToggleFilter(filteredPeople[0]);
                  setIsSearchOpen(false);
                }
              }
            }}
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput('')}
              className="absolute right-2.5 text-slate-400 hover:text-white p-0.5 rounded-full hover:bg-white/10"
              title="Suche leeren"
            >
              <X size={12} />
            </button>
          )}
        </div>

        {/* Interactive Direct Search Results List */}
        <div className="max-h-64 overflow-y-auto custom-scrollbar flex flex-col gap-1 -mx-1 px-1">
          {filteredPeople.slice(0, 12).map(person => {
            const detail = peopleData?.get(person);
            const isSelected = filterAssignee === person;
            const hasActive = activeVorgangNames ? activeVorgangNames.has(person.toLowerCase()) : Boolean(detail?.hasActiveVorgang);
            const avatarGrad = getAvatarGradient(detail?.organization, person);

            const rawParts = person.split(/[\s.,/\\()_+\-&]+/).filter(Boolean);
            const cleanParts = rawParts.filter(p => !stopWords.has(p.toLowerCase()));
            const parts = cleanParts.length > 0 ? cleanParts : rawParts;
            const initials = parts.length === 0 ? '??' : parts.length === 1 ? parts[0].substring(0, 2).toUpperCase() : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();

            return (
              <div
                key={person}
                onClick={() => {
                  onToggleFilter(person);
                  setIsSearchOpen(false);
                }}
                className={`group flex items-center justify-between p-2 rounded-xl cursor-pointer transition-all ${
                  isSelected 
                    ? 'bg-indigo-600 text-white shadow-md' 
                    : 'hover:bg-slate-800/80 text-slate-200'
                }`}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-white font-bold text-[11px] shadow-sm relative"
                    style={{ background: avatarGrad }}
                  >
                    {initials}
                    {hasActive && (
                      <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-amber-400 border-2 border-slate-900 shadow-xs animate-pulse" />
                    )}
                  </div>
                  <div className="min-w-0 flex flex-col">
                    <span className="text-xs font-semibold truncate leading-tight">
                      {person}
                    </span>
                    {(detail?.organization || detail?.role) && (
                      <span className={`text-[10px] truncate leading-tight mt-0.5 ${isSelected ? 'text-indigo-200' : 'text-slate-400'}`}>
                        {[detail.organization, detail.role].filter(Boolean).join(' • ')}
                      </span>
                    )}
                  </div>
                </div>

                <div className="shrink-0 flex items-center gap-1.5 ml-2">
                  {hasActive && (
                    <span className={`text-[9.5px] px-1.5 py-0.5 rounded-md font-medium ${
                      isSelected ? 'bg-indigo-700/80 text-amber-200' : 'bg-amber-500/15 text-amber-300 border border-amber-500/20'
                    }`}>
                      in Aktion
                    </span>
                  )}
                  {isSelected ? (
                    <Check size={14} className="text-white shrink-0" />
                  ) : (
                    <span className="text-[10px] text-slate-500 group-hover:text-indigo-300 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      Filtern
                    </span>
                  )}
                </div>
              </div>
            );
          })}

          {filteredPeople.length > 12 && (
            <div className="text-center py-1 text-[10px] text-slate-400">
              + {filteredPeople.length - 12} weitere Personen in der Leiste sichtbar
            </div>
          )}

          {filteredPeople.length === 0 && (
            <div className="py-6 text-center flex flex-col items-center justify-center text-slate-500 gap-1">
              <SearchX size={20} className="text-slate-600 mb-1" />
              <span className="text-xs font-semibold text-slate-400">Keine Person gefunden</span>
              <span className="text-[10px] text-slate-500">Für „{searchInput}“ gibt es keine Treffer</span>
              <button
                type="button"
                onClick={() => setSearchInput('')}
                className="mt-2 px-3 py-1 rounded-lg bg-white/10 hover:bg-white/15 text-slate-300 text-xs font-medium transition-colors"
              >
                Suche leeren
              </button>
            </div>
          )}
        </div>

        {/* Footer Shortcuts */}
        <div className="pt-2 border-t border-white/10 flex items-center justify-between text-[10px] text-slate-400 px-0.5">
          <span><kbd className="px-1 py-0.5 rounded bg-slate-800 text-slate-300 border border-white/10 font-mono text-[9px]">Enter</kbd> Wählen</span>
          <span><kbd className="px-1 py-0.5 rounded bg-slate-800 text-slate-300 border border-white/10 font-mono text-[9px]">Esc</kbd> Schließen</span>
          {isFilterActive && (
            <button
              type="button"
              onClick={() => {
                onClear();
                setIsSearchOpen(false);
              }}
              className="text-rose-400 hover:text-rose-300 font-medium"
            >
              Filter aufheben
            </button>
          )}
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};
